/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useCallback } from 'react';
import { useLazyQuery } from '@apollo/react-hooks';

import { useSessionData } from '@confluence/session-data';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { useIsAllowedToCreateWhiteboard } from '@confluence/content-types-utils';
import { fg } from '@confluence/feature-gating';
import { preloadContextualCreateSubtextEnrollment } from '@confluence/onboarding-helpers/entry-points/preloadContextualCreateSubtextEnrollment';

import type { usePreloadCreationMenuDataWhiteboardCreationPaywallContentToDisableQuery as usePreloadCreationMenuDataWhiteboardCreationPaywallContentToDisableQuery$data } from './__types__/usePreloadCreationMenuDataWhiteboardCreationPaywallContentToDisableQuery';

const usePreloadCreationMenuDataWhiteboardCreationPaywallContentToDisableQuery = gql`
	query usePreloadCreationMenuDataWhiteboardCreationPaywallContentToDisableQuery {
		paywallContentToDisable(contentType: "whiteboard") {
			deactivationIdentifier
			link
			title
		}
	}
`;
type UsePreloadCreationMenuDataResponse = () => void;
type UsePreloadCreationMenuDataProp = {
	isContextual?: boolean;
};
export const usePreloadCreationMenuData = ({
	isContextual = false,
}: UsePreloadCreationMenuDataProp = {}): UsePreloadCreationMenuDataResponse => {
	const { edition } = useSessionData();
	const isPremium = edition === ConfluenceEdition.PREMIUM;

	const isAllowedToCreateWhiteboard = useIsAllowedToCreateWhiteboard();
	const [preloadWhiteboardPaywallData] =
		useLazyQuery<usePreloadCreationMenuDataWhiteboardCreationPaywallContentToDisableQuery$data>(
			usePreloadCreationMenuDataWhiteboardCreationPaywallContentToDisableQuery,
		);
	const shouldPreloadPaywallData = isAllowedToCreateWhiteboard && !isPremium;
	return useCallback(() => {
		if (shouldPreloadPaywallData) {
			preloadWhiteboardPaywallData();
		}

		if (isContextual && fg('cc_contextual_create_menu_subtext_kill_switch')) {
			void preloadContextualCreateSubtextEnrollment();
		}
	}, [preloadWhiteboardPaywallData, shouldPreloadPaywallData, isContextual]);
};
